/**
 * FileService - класс для эмуляции файловой системы через localStorage
 * Предоставляет интерфейс для работы с данными, как будто они хранятся в файлах
 */

// Эмуляция файловой системы через localStorage 
// (поскольку в браузере нет доступа к реальной файловой системе)

class FileService {
  constructor() {
    this.filePrefix = 'file_h2h_';
    console.log('✅ Инициализация FileService для сохранения H2H данных');
    this._ensureStorageAvailable();
  }

  // Проверка доступности localStorage
  _ensureStorageAvailable() {
    if (typeof localStorage === 'undefined') {
      console.error('❌ localStorage недоступен в этом браузере');
      return false;
    }
    
    try {
      const testKey = '__test_storage__';
      localStorage.setItem(testKey, '1');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      console.error('❌ localStorage недоступен:', e);
      return false;
    }
  }

  // Создание ключа для хранения файла
  _createFileKey(team1, team2) {
    return `${this.filePrefix}${team1}-${team2}`;
  }

  // Сохранение данных H2H в "файл" (localStorage)
  saveH2HDataToFile(team1, team2, data) {
    try {
      if (!team1 || !team2 || !data) {
        console.error('❌ Неверные параметры для сохранения данных H2H');
        return false;
      }

      // Создаем ключ для хранения
      const fileKey = this._createFileKey(team1, team2);

      // Добавляем метку времени к данным
      const dataWithTimestamp = {
        ...data,
        timestamp: data.timestamp || new Date().toISOString(),
        fromFile: true
      };

      // Записываем данные в localStorage
      localStorage.setItem(fileKey, JSON.stringify(dataWithTimestamp));
      console.log(`✅ Данные H2H для команд ${team1} и ${team2} сохранены в "файл" (localStorage)`);
      return true;
    } catch (error) {
      console.error(`❌ Ошибка при сохранении данных H2H:`, error);
      return false;
    }
  }

  // Загрузка данных H2H из "файла" (localStorage)
  loadH2HDataFromFile(team1, team2) {
    try {
      if (!team1 || !team2) {
        console.error('❌ Неверные параметры для загрузки данных H2H');
        return null;
      }

      // Проверяем существование данных для team1-team2
      const fileKey = this._createFileKey(team1, team2);
      // Также проверяем обратный порядок team2-team1
      const fileKeyReverse = this._createFileKey(team2, team1);

      let fileData = localStorage.getItem(fileKey);
      let dataSource = fileKey;
      
      // Если данных с прямым порядком нет, проверяем обратный порядок
      if (!fileData) {
        fileData = localStorage.getItem(fileKeyReverse);
        dataSource = fileKeyReverse;
      }

      // Если данных нет вообще
      if (!fileData) {
        console.log(`❌ "Файл" с данными H2H для команд ${team1} и ${team2} не найден`);
        return null;
      }
      
      // Парсим данные
      const data = JSON.parse(fileData);
      console.log(`✅ Данные H2H для команд загружены из "файла" ${dataSource}`);

      // Проверяем актуальность данных (не старше 7 дней)
      if (data && data.timestamp) {
        const dataDate = new Date(data.timestamp);
        const now = new Date();
        const diffDays = Math.ceil((now - dataDate) / (1000 * 60 * 60 * 24));
        
        if (diffDays > 7) {
          console.log(`⚠️ Данные H2H для команд ${team1} и ${team2} устарели (${diffDays} дней)`);
          return null;
        }
      }

      return data;
    } catch (error) {
      console.error(`❌ Ошибка при загрузке данных H2H из "файла":`, error);
      return null;
    }
  }

  // Очистка "файла" H2H для конкретных команд
  clearH2HFile(team1, team2) {
    try {
      if (!team1 || !team2) {
        console.error('❌ Неверные параметры для очистки "файла" H2H');
        return false;
      }

      // Проверяем существование данных для team1-team2
      const fileKey = this._createFileKey(team1, team2);
      // Также проверяем обратный порядок team2-team1
      const fileKeyReverse = this._createFileKey(team2, team1);

      let success = false;

      // Удаляем данные, если они существуют
      if (localStorage.getItem(fileKey)) {
        localStorage.removeItem(fileKey);
        console.log(`✅ "Файл" H2H для команд ${team1} и ${team2} успешно удален`);
        success = true;
      }

      if (localStorage.getItem(fileKeyReverse)) {
        localStorage.removeItem(fileKeyReverse);
        console.log(`✅ "Файл" H2H для команд ${team2} и ${team1} успешно удален`);
        success = true;
      }

      if (!success) {
        console.log(`⚠️ "Файл" H2H для команд ${team1} и ${team2} не найден`);
      }

      return success;
    } catch (error) {
      console.error(`❌ Ошибка при удалении "файла" H2H:`, error);
      return false;
    }
  }

  // Очистка всех "файлов" H2H
  clearAllH2HFiles() {
    try {
      // Получаем список всех ключей в localStorage
      const fileKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.filePrefix)) {
          fileKeys.push(key);
        }
      }
      
      if (fileKeys.length === 0) {
        console.log('⚠️ "Файлы" H2H не найдены');
        return false;
      }

      // Удаляем каждый "файл"
      fileKeys.forEach(key => {
        localStorage.removeItem(key);
        console.log(`✅ "Файл" ${key} успешно удален`);
      });

      console.log(`✅ Все "файлы" H2H успешно удалены (${fileKeys.length} файлов)`);
      return true;
    } catch (error) {
      console.error(`❌ Ошибка при удалении "файлов" H2H:`, error);
      return false;
    }
  }

  // Получение списка всех "файлов" H2H
  getH2HFilesList() {
    try {
      // Получаем список всех ключей в localStorage
      const filesList = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.filePrefix)) {
          // Извлекаем ID команд из ключа
          const teamsStr = key.replace(this.filePrefix, '');
          const [team1, team2] = teamsStr.split('-');
          
          // Получаем данные
          const fileData = localStorage.getItem(key);
          let timestamp = null;
          let size = fileData ? fileData.length : 0;
          
          try {
            const parsedData = JSON.parse(fileData);
            timestamp = parsedData.timestamp;
          } catch (e) {
            console.error(`❌ Ошибка парсинга данных для ${key}:`, e);
          }
          
          filesList.push({
            key,
            teams: [parseInt(team1), parseInt(team2)],
            size,
            timestamp,
            modified: timestamp ? new Date(timestamp) : new Date()
          });
        }
      }

      console.log(`✅ Получен список H2H файлов (${filesList.length}):`, filesList);
      return filesList;
    } catch (error) {
      console.error(`❌ Ошибка при получении списка "файлов" H2H:`, error);
      return [];
    }
  }

  // Сохраняет данные в "файл" (localStorage с префиксом)
  saveData(fileName, data) {
    try {
      const key = this.filePrefix + fileName;
      localStorage.setItem(key, JSON.stringify(data));
      console.log(`Данные сохранены в localStorage: ${key}`);
      return true;
    } catch (error) {
      console.error(`Ошибка при сохранении данных в localStorage для ${fileName}:`, error);
      // Проверяем, связана ли ошибка с переполнением localStorage
      if (error instanceof DOMException && (error.name === 'QuotaExceededError' || 
          error.name === 'NS_ERROR_DOM_QUOTA_REACHED')) {
        console.warn('localStorage переполнен. Попытка очистить старые данные...');
        this.cleanupOldData();
      }
      return false;
    }
  }

  // Загружает данные из "файла" (localStorage с префиксом)
  loadData(fileName) {
    try {
      const key = this.filePrefix + fileName;
      const data = localStorage.getItem(key);
      if (data) {
        return JSON.parse(data);
      }
      console.log(`Данные не найдены в localStorage: ${key}`);
      return null;
    } catch (error) {
      console.error(`Ошибка при загрузке данных из localStorage для ${fileName}:`, error);
      return null;
    }
  }

  // Проверяет существование "файла"
  fileExists(fileName) {
    const key = this.filePrefix + fileName;
    return localStorage.getItem(key) !== null;
  }

  // Удаляет "файл"
  deleteFile(fileName) {
    try {
      const key = this.filePrefix + fileName;
      localStorage.removeItem(key);
      console.log(`Данные удалены из localStorage: ${key}`);
      return true;
    } catch (error) {
      console.error(`Ошибка при удалении данных из localStorage для ${fileName}:`, error);
      return false;
    }
  }

  // Удаляет все "файлы" (записи в localStorage с префиксом)
  clearCache() {
    try {
      const keys = this.getCacheFiles(true);
      keys.forEach(key => {
        localStorage.removeItem(key);
      });
      console.log('Все файлы кэша удалены из localStorage');
      return true;
    } catch (error) {
      console.error('Ошибка при очистке кэша из localStorage:', error);
      return false;
    }
  }

  // Получает список всех "файлов" (или ключей, если withPrefix=true)
  getCacheFiles(withPrefix = false) {
    try {
      return Object.keys(localStorage)
        .filter(key => key.startsWith(this.filePrefix))
        .map(key => withPrefix ? key : key.replace(this.filePrefix, ''));
    } catch (error) {
      console.error('Ошибка при получении списка файлов кэша:', error);
      return [];
    }
  }

  // Получает размер кэша в байтах
  getCacheSize() {
    try {
      let totalSize = 0;
      const keys = this.getCacheFiles(true);
      
      keys.forEach(key => {
        const value = localStorage.getItem(key) || '';
        totalSize += key.length * 2 + value.length * 2; // Примерный размер в байтах (UTF-16)
      });
      
      return this.formatSize(totalSize);
    } catch (error) {
      console.error('Ошибка при получении размера кэша:', error);
      return 'Неизвестно';
    }
  }

  // Очистка старых данных при переполнении localStorage
  cleanupOldData() {
    try {
      // Получаем все ключи файлов
      const fileKeys = this.getCacheFiles(true);
      
      // Если файлов мало, нечего чистить
      if (fileKeys.length <= 2) return false;
      
      console.log(`Найдено ${fileKeys.length} файлов для возможной очистки`);
      
      // Удаляем 1/3 самых старых файлов 
      // (предполагая, что самые старые - те, которые реже используются)
      const filesToRemove = Math.ceil(fileKeys.length / 3);
      
      for (let i = 0; i < filesToRemove; i++) {
        localStorage.removeItem(fileKeys[i]);
        console.log(`Очистка localStorage: удален ${fileKeys[i]}`);
      }
      
      return true;
    } catch (error) {
      console.error('Ошибка при очистке старых данных:', error);
      return false;
    }
  }

  // Форматирует размер в байтах в удобочитаемый формат
  formatSize(bytes) {
    if (bytes < 1024) {
      return `${bytes} байт`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} КБ`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(2)} МБ`;
    }
  }
}

export default new FileService(); 